import {
  Badge,
  Box,
  Button,
  Card,
  HStack,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { format } from "date-fns"
import { FiTrash2 } from "react-icons/fi"
import { HtmlContent } from "../../components/Common/HtmlContent"

import type { UserPublic } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useFeedbackEnabled } from "@/hooks/useFeedbackEnabled"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import {
  type FeedbackByQuiz,
  FeedbackServiceTemp,
} from "@/services/feedbackService"

export const Route = createFileRoute("/_layout/my-feedback")({
  component: MyFeedbackPage,
})

function MyFeedbackPage() {
  const { feedbackLabel } = useStudentLabels()
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const { enabled: feedbackEnabled } = useFeedbackEnabled()
  const breadcrumbs = useRouteBreadcrumbs("feedback.mine")

  const { data: feedbackData, isLoading } = useQuery<FeedbackByQuiz>({
    queryKey: ["my-feedback", currentUser?.id],
    queryFn: () => FeedbackServiceTemp.getMyFeedback(),
    enabled: !!currentUser && feedbackEnabled,
  })

  if (!feedbackEnabled) {
    return (
      <PageShell
        title={feedbackLabel}
        breadcrumbs={breadcrumbs}
        description="Feedback is disabled for your organization."
      >
        <AppEmptyState
          title={emptyStateCopy.feedback.disabled.title}
          description={emptyStateCopy.feedback.disabled.description}
          data-testid="my-feedback-disabled-state"
        />
      </PageShell>
    )
  }

  if (isLoading) {
    return (
      <PageShell
        title={feedbackLabel}
        breadcrumbs={breadcrumbs}
        description="Review feedback you've submitted on quizzes."
      >
        <Box data-testid="my-feedback-loading">
          <PageSkeleton variant="detail" rows={4} />
        </Box>
      </PageShell>
    )
  }

  const quizNames = Object.keys(feedbackData || {})

  if (quizNames.length === 0) {
    return (
      <PageShell
        title={feedbackLabel}
        breadcrumbs={breadcrumbs}
        description="Review feedback you've submitted on quizzes."
      >
        <AppEmptyState
          title={emptyStateCopy.feedback.mineEmpty.title}
          description={emptyStateCopy.feedback.mineEmpty.description}
          data-testid="my-feedback-empty-state"
        />
      </PageShell>
    )
  }

  return (
    <PageShell
      title={feedbackLabel}
      breadcrumbs={breadcrumbs}
      description="Review feedback you've submitted on quizzes."
    >
      <Stack gap={8}>
        {quizNames.map((quizName) => {
          const feedbackList = feedbackData![quizName]
          return (
            <Box key={quizName}>
              <Heading size="lg" mb={4}>
                {quizName}
              </Heading>
              <Stack gap={4}>
                {feedbackList.map((feedback) => (
                  <Card.Root key={feedback.id}>
                    <Card.Body>
                      <Stack gap={4}>
                        {/* Header */}
                        <HStack justify="space-between">
                          <HStack gap={2}>
                            <Badge
                              colorPalette={
                                feedback.status === "resolved"
                                  ? "green"
                                  : feedback.status === "read"
                                    ? "blue"
                                    : "orange"
                              }
                            >
                              {feedback.status}
                            </Badge>
                            <Text fontSize="sm" color="fg.muted">
                              {format(
                                new Date(feedback.created_at),
                                "MMM d, yyyy 'at' h:mm a",
                              )}
                            </Text>
                          </HStack>
                          {feedback.status !== "resolved" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                            >
                              <FiTrash2 />
                              Delete
                            </Button>
                          )}
                        </HStack>

                        {/* Feedback content */}
                        <Box>
                          <Text fontWeight="semibold" mb={2}>
                            Your Feedback:
                          </Text>
                          <HtmlContent content={feedback.feedback_text} />
                        </Box>

                        {/* Replies */}
                        {feedback.replies.length > 0 && (
                          <Box
                            pl={6}
                            borderLeftWidth="2px"
                            borderLeftColor="border"
                          >
                            <Text fontWeight="semibold" mb={2}>
                              Teacher Replies:
                            </Text>
                            <Stack gap={3}>
                              {feedback.replies.map((reply) => (
                                <Box key={reply.id}>
                                  <Text fontSize="sm" color="fg.muted" mb={1}>
                                    {format(
                                      new Date(reply.created_at),
                                      "MMM d, yyyy 'at' h:mm a",
                                    )}
                                    {reply.is_published && (
                                      <Badge
                                        ml={2}
                                        colorPalette="blue"
                                        size="sm"
                                      >
                                        Published
                                      </Badge>
                                    )}
                                  </Text>
                                  <HtmlContent content={reply.feedback_text} />
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Actions */}
                        <HStack>
                          <Link
                            to="/quizzes/$quizId"
                            params={{ quizId: feedback.quiz_id }}
                          >
                            <Button size="sm" variant="outline">
                              View Quiz
                            </Button>
                          </Link>
                        </HStack>
                      </Stack>
                    </Card.Body>
                  </Card.Root>
                ))}
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </PageShell>
  )
}
